import React, { useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { Sparkles, ShieldCheck, Clock } from "lucide-react";
import { toast, Toaster } from "sonner";

const benefits = [
  { icon: Sparkles, t1: "Stima personalizzata", t1en: "Tailored estimate", t2: "Analisi sui dati di mercato della tua zona, non un algoritmo generico.", t2en: "Based on real market data in your area, not a generic algorithm." },
  { icon: ShieldCheck, t1: "Senza impegno", t1en: "No obligation", t2: "Il servizio è gratuito e non ti vincola in nessun modo.", t2en: "The service is free and does not bind you in any way." },
  { icon: Clock, t1: "Risposta in 24 ore", t1en: "Reply within 24h", t2: "Un nostro consulente locale ti contatta entro un giorno lavorativo.", t2en: "A local consultant contacts you within one working day." },
];

const EvaluationPage = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", property_type: "apartment", address: "", area_sqm: "", rooms: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, area_sqm: form.area_sqm ? Number(form.area_sqm) : null, rooms: form.rooms ? Number(form.rooms) : null };
      await api.post("/evaluations", payload);
      toast.success(t.evaluation.success);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", property_type: "apartment", address: "", area_sqm: "", rooms: "", notes: "" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error(t.evaluation.error);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full bg-[#FDFBF7] border border-[#E8DFD8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5A3C]/30 focus:border-[#8B5A3C] transition-all";
  const labelClass = "block text-[11px] uppercase tracking-[0.18em] font-bold text-[#5C544B] mb-2";

  return (
    <div data-testid="evaluation-page" className="fade-in">
      <Toaster position="top-right" richColors />
      <section className="relative bg-[#2C2621] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.pexels.com/photos/31854903/pexels-photo-31854903.jpeg?auto=compress&cs=tinysrgb&w=2000" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#CBA37E] mb-5">{t.evaluation.eyebrow}</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] tracking-tight max-w-3xl mb-7">
            {t.evaluation.title}
          </h1>
          <p className="text-white/85 text-lg max-w-2xl leading-relaxed">{t.evaluation.subtitle}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <div className="space-y-7 lg:sticky lg:top-28">
            {benefits.map((v, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-[#F3E6DA] flex items-center justify-center shrink-0">
                  <v.icon size={18} className="text-[#8B5A3C]" />
                </div>
                <div>
                  <h3 className="font-display text-xl text-[#2C2621] mb-1.5">{lang === "it" ? v.t1 : v.t1en}</h3>
                  <p className="text-sm text-[#5C544B] leading-relaxed">{lang === "it" ? v.t2 : v.t2en}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3">
          <form onSubmit={submit} data-testid="evaluation-form" className="bg-white border border-[#E8DFD8] rounded-[2rem] p-8 md:p-10 shadow-sm">
            <h2 className="font-display text-3xl text-[#2C2621] mb-2">{t.evaluation.formTitle}</h2>
            <p className="text-sm text-[#5C544B] mb-8">{t.evaluation.formSubtitle}</p>

            {success && (
              <div className="bg-[#5E6B56]/10 border border-[#5E6B56]/30 text-[#5E6B56] rounded-xl p-4 text-sm mb-6" data-testid="evaluation-success">
                {t.evaluation.success}
              </div>
            )}

            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>{t.evaluation.name}</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} data-testid="eval-name" />
                </div>
                <div>
                  <label className={labelClass}>{t.evaluation.phone}</label>
                  <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} data-testid="eval-phone" />
                </div>
              </div>
              <div>
                <label className={labelClass}>{t.evaluation.email}</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} data-testid="eval-email" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>{t.evaluation.propertyType}</label>
                  <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className={inputClass} data-testid="eval-type">
                    <option value="apartment">{t.filters.apartment}</option>
                    <option value="villa">{t.filters.villa}</option>
                    <option value="house">{t.filters.house}</option>
                    <option value="room">{t.filters.room}</option>
                    <option value="commercial">{t.filters.commercial}</option>
                    <option value="land">{t.filters.land}</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>{t.evaluation.address}</label>
                  <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputClass} data-testid="eval-address" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>{t.evaluation.area}</label>
                  <input type="number" value={form.area_sqm} onChange={(e) => setForm({ ...form, area_sqm: e.target.value })} className={inputClass} data-testid="eval-area" />
                </div>
                <div>
                  <label className={labelClass}>{t.evaluation.rooms}</label>
                  <input type="number" value={form.rooms} onChange={(e) => setForm({ ...form, rooms: e.target.value })} className={inputClass} data-testid="eval-rooms" />
                </div>
              </div>
              <div>
                <label className={labelClass}>{t.evaluation.notes}</label>
                <textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputClass} resize-none`} data-testid="eval-notes" />
              </div>

              <button disabled={submitting} type="submit" data-testid="eval-submit" className="w-full bg-[#8B5A3C] text-white hover:bg-[#704830] disabled:opacity-60 transition-colors rounded-full py-3.5 font-medium">
                {submitting ? "…" : t.evaluation.submit}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default EvaluationPage;
